/**
 * Treatment Plan Master API (Phase 2.7)
 *
 * Workflow plans authored by doctors — supersedes Phase 2.6 plan templates.
 * Endpoints sit under /api/organisation/treatment-plans/* (NOT to be
 * confused with /plans/* which is the patient-instance namespace).
 *
 *   POST   /treatment-plans                               create
 *   GET    /treatment-plans?include_inactive=             list (scoped)
 *   GET    /treatment-plans/{id}?member_id=               get one
 *   PUT    /treatment-plans/{id}?member_id=               full replace
 *   DELETE /treatment-plans/{id}?member_id=               soft delete
 *   POST   /treatment-plans/{id}/attach-to-visit          materialise PatientPlan
 *
 * `attachToVisit` may return a 400 with structured body
 *   { code: "MISSING_ASSIGNEES", step_ids: [...], missing_member_ids: [...], message }
 * — the AttachPlanModal renders the offending rows + a "Default all to me"
 * recovery button.
 *
 * Every function returns `{ success, data?, error?, status?, errorCode?, errorPayload? }`.
 */

import apiClient from './apiClient';


function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const status = error?.response?.status;
  const serverDetail = data?.detail;
  if (serverDetail && typeof serverDetail === 'object') {
    if (serverDetail.message) return serverDetail.message;
    return JSON.stringify(serverDetail);
  }
  if (typeof serverDetail === 'string') return serverDetail;
  if (data?.message) return data.message;

  switch (status) {
    case 400: return 'Invalid request — please check the form.';
    case 401: return 'Session expired. Please log in again.';
    case 403: return 'You do not have permission for this action.';
    case 404: return 'Treatment plan not found.';
    case 409: return 'Conflict — please refresh and try again.';
    case 422: return 'Invalid data — please check the form.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

function ok(response) {
  return { success: true, data: response.data };
}

function fail(error, fallback) {
  console.error('[TreatmentPlanMasterAPI]', error);
  // Surface structured error codes (MISSING_ASSIGNEES) so callers can
  // route to recovery UI instead of just a toast.
  const detail = error?.response?.data?.detail;
  const errorCode =
    detail && typeof detail === 'object' ? detail.code : undefined;
  return {
    success: false,
    error: buildApiError(error, fallback),
    // `status` is surfaced (additive, non-breaking) so callers can distinguish
    // guard rejections — e.g. AttachPlanModal maps a bare 403 out-of-department
    // / 400 role-ineligible into a readable eligibility hint (A2-S6). The
    // server `detail`/`message` still passes through verbatim via `error`.
    status: error?.response?.status,
    errorCode,
    errorPayload: detail && typeof detail === 'object' ? detail : undefined,
  };
}


// ── Master CRUD ─────────────────────────────────────────────────────

/**
 * @param {object} body  CreateTreatmentPlanMasterRequest payload.
 * @param {object} [opts]
 * @param {string} [opts.ownerDoctorMemberId]  Admin-only — when an admin
 *   authors a plan for another doctor, pass the target doctor's
 *   `member_id` so the backend stamps ownership accordingly. Doctors
 *   omit (defaults to caller's own).
 */
export const createPlanMaster = async (body, { ownerDoctorMemberId } = {}) => {
  try {
    const params = {};
    if (ownerDoctorMemberId) params.owner_doctor_member_id = ownerDoctorMemberId;
    const r = await apiClient.post(
      '/api/organisation/treatment-plans', body, { params },
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to create treatment plan.');
  }
};

export const listPlanMasters = async ({ includeInactive = false } = {}) => {
  try {
    const params = {};
    if (includeInactive) params.include_inactive = true;
    const r = await apiClient.get(
      '/api/organisation/treatment-plans', { params },
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to load treatment plans.');
  }
};

export const getPlanMaster = async (planId, { memberId, includeInactive = false } = {}) => {
  try {
    const params = {};
    if (memberId) params.member_id = memberId;
    if (includeInactive) params.include_inactive = true;
    const r = await apiClient.get(
      `/api/organisation/treatment-plans/${planId}`, { params },
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to load treatment plan.');
  }
};

export const updatePlanMaster = async (planId, body, { memberId } = {}) => {
  try {
    const params = {};
    if (memberId) params.member_id = memberId;
    const r = await apiClient.put(
      `/api/organisation/treatment-plans/${planId}`, body, { params },
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to update treatment plan.');
  }
};

export const deletePlanMaster = async (planId, { memberId } = {}) => {
  try {
    const params = {};
    if (memberId) params.member_id = memberId;
    const r = await apiClient.delete(
      `/api/organisation/treatment-plans/${planId}`, { params },
    );
    return { success: true, data: r.data ?? null };
  } catch (err) {
    return fail(err, 'Failed to delete treatment plan.');
  }
};


// ── Attach to a patient visit (materialise PatientPlan) ─────────────

/**
 * @param {string} sourcePlanId  — the workflow master to attach.
 * @param {object} body
 *   - patient_id (required)
 *   - source_visit_id?
 *   - owner_doctor_member_id?  // admin-only: attach another doctor's master
 *   - excluded_step_ids?: string[]
 *   - assignee_resolutions?: { [step_id]: { executor_member_id, approver_member_id? } }
 *   - plan_title?
 *
 * On 400 with errorCode=MISSING_ASSIGNEES, errorPayload carries the
 * step_ids the modal should highlight.
 */
export const attachPlanToVisit = async (sourcePlanId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/treatment-plans/${sourcePlanId}/attach-to-visit`,
      body,
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to attach plan to visit.');
  }
};


const treatmentPlanMasterApi = {
  createPlanMaster,
  listPlanMasters,
  getPlanMaster,
  updatePlanMaster,
  deletePlanMaster,
  attachPlanToVisit,
};

export default treatmentPlanMasterApi;
